import React, { useCallback, useMemo, useState } from 'react';

import { createStyles } from '@mantine/core';
import {
  Eventcalendar,
  MbscCalendarEvent,
  MbscCellClickEvent,
  MbscEventClickEvent,
  MbscPageLoadingEvent,
} from '@mobiscroll/react';
import dayjs from 'dayjs';
import useAppointmentAgenda from 'hooks/appointment/useAppointmentAgenda';
import useClassAgenda from 'hooks/class/useClassAgenda';
import useAutoCompleteStaff from 'hooks/staff/useAutoCompleteStaff';
import useStaffAvailabilityCalendarParallel from 'hooks/staffAvailability/useStaffAvailabilityCalendarParallel';
import useDebounce from 'hooks/useDebounce';
import { AppointmentAgenda, KiloClassAgenda, StaffAutoComplete } from 'types';
import { isDateTimePast } from 'utils/date-functions';
import { getAppointmentEventTitle } from 'utils/event.helpers';
import { sortArray } from 'utils/functions';
import { getEventResourceIds, withNoCoachResource } from 'utils/scheduling.helpers';

import type { LoadStatus } from './EventCalendarNavigationButtons';
import ModalDragNotificationConfirmation from './ModalDragNotificationConfirmation';
import ModalDragRecurrenceConfirmation from './ModalDragRecurrenceConfirmation';
import type { SelectedCoach } from '../EventCalendar';
import useEventDragUpdate, { DragPrefill } from '../hooks/useEventDragUpdate';

interface CalendarEventWithMeta extends MbscCalendarEvent {
  eventType: 'class' | 'appointment';
  originalId: string | number;
}

interface CoachResource {
  id: string;
  name: string;
}

const useStyles = createStyles(theme => ({
  calendar: {
    fontFamily: `${theme.fontFamily} !important`,
    borderRadius: theme.radius.md,
    '& .mbsc-schedule-date-header': {
      display: 'none !important',
    },
    '& .mbsc-schedule-col-width': {
      minWidth: 200,
    },
    '& .mbsc-schedule-color': {
      zIndex: 1,
      pointerEvents: 'none',
      clipPath: 'inset(2px calc(100% - 6px) 2px 2px round 4px)',
    },
    '& .mbsc-schedule-invalid': {
      left: '4px !important',
      width: 'calc(100% - 8px) !important',
      borderRadius: theme.radius.md,
    },
    '& .mbsc-schedule-event': {
      zIndex: 4,
      marginLeft: '6px',
    },
    '& .mbsc-schedule-event-resize-start': {
      display: 'none !important',
    },
    '& .mbsc-schedule-event-title': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
}));

interface FilterTerms {
  eventTypes: string[];
  coaches: string[];
  locations: string[];
}

interface IEventCalendarDaySchedulerViewProps {
  eventCalendarNavigationButtons: (
    eventsStatus?: LoadStatus,
    coachAvailabilityStatus?: LoadStatus,
  ) => JSX.Element;
  searchFilters: FilterTerms;
  createEventDate: Date;
  setCreateEventDate: React.Dispatch<React.SetStateAction<Date>>;
  calendarMaxDate?: Date;
  handleEditClassEvent: (kcId: string, prefill?: DragPrefill) => void;
  handleEditAppointmentEvent: (aId: string, prefill?: DragPrefill) => void;
  handleOpenSelectEventModal: () => void;
  permission?: boolean;
  setSelectedCoach: React.Dispatch<React.SetStateAction<SelectedCoach | undefined>>;
}

const getLoadStatus = (isError: boolean, isSuccess: boolean): LoadStatus => {
  if (isError) return 'error';
  if (isSuccess) return 'loaded';
  return 'loading';
};

const getCoachIdsForClass = (event: KiloClassAgenda): string[] =>
  (event?.kiloClassCoachKiloClassCoachRoles ?? []).reduce<string[]>((acc, r) => {
    const id = r?.coach?.staffId;
    if (id != null) acc.push(id);
    return acc;
  }, []);

const getCoachIdsForAppointment = (event: AppointmentAgenda): string[] =>
  (event?.appointmentCoachAppointmentCoachRoles ?? []).reduce<string[]>((acc, r) => {
    const id = r?.coach?.staffId;
    if (id != null) acc.push(id);
    return acc;
  }, []);

const isClassDraggable = (event: KiloClassAgenda): boolean => {
  if (isDateTimePast(event.startDate, event.startTime)) return false;
  return !event.athleteKiloClasses.some(akc => akc.isCheckedIn || akc.isLateCancel || akc.isNoShow);
};

const isAppointmentDraggable = (event: AppointmentAgenda): boolean => {
  if (isDateTimePast(event.startDate, event.startTime)) return false;
  return !event.athleteAppointments.some(aa => aa.isCheckedIn || aa.isLateCancel || aa.isNoShow);
};

interface AvailabilitySlot {
  start: string;
  end: string;
  resource: string;
}

const formatAvailabilitySlot = (
  start: dayjs.Dayjs,
  end: dayjs.Dayjs,
  resource: string,
): AvailabilitySlot => ({
  start: start.format('YYYY-MM-DDTHH:mm'),
  end: end.format('YYYY-MM-DDTHH:mm'),
  resource,
});

const getResourceAvailableSlots = (
  resourceUnavailable: AvailabilitySlot[],
  resourceId: string,
  dayStart: dayjs.Dayjs,
  dayEnd: dayjs.Dayjs,
): AvailabilitySlot[] => {
  if (resourceUnavailable.length === 0) {
    return [formatAvailabilitySlot(dayStart, dayEnd, resourceId)];
  }

  const { slots, currentTime } = resourceUnavailable.reduce<{
    slots: AvailabilitySlot[];
    currentTime: dayjs.Dayjs;
  }>(
    (acc, slot) => {
      const slotStart = dayjs(slot.start);
      const slotEnd = dayjs(slot.end);
      const newSlots = acc.currentTime.isBefore(slotStart)
        ? [...acc.slots, formatAvailabilitySlot(acc.currentTime, slotStart, resourceId)]
        : acc.slots;
      const nextTime = slotEnd.isAfter(acc.currentTime) ? slotEnd : acc.currentTime;
      return { slots: newSlots, currentTime: nextTime };
    },
    { slots: [], currentTime: dayStart },
  );

  return currentTime.isBefore(dayEnd)
    ? [...slots, formatAvailabilitySlot(currentTime, dayEnd, resourceId)]
    : slots;
};

const calculateAvailableSlots = (
  unavailableSlots: AvailabilitySlot[],
  resourceIds: string[],
  selectedDate: string,
): AvailabilitySlot[] => {
  const dayStart = dayjs(selectedDate).startOf('day');
  const dayEnd = dayjs(selectedDate).endOf('day');

  return resourceIds.flatMap(resourceId => {
    const resourceUnavailable = unavailableSlots
      .filter(slot => slot.resource === resourceId)
      .sort((a, b) => dayjs(a.start).valueOf() - dayjs(b.start).valueOf());

    return getResourceAvailableSlots(resourceUnavailable, resourceId, dayStart, dayEnd);
  });
};

const EventCalendarDaySchedulerView = ({
  eventCalendarNavigationButtons,
  searchFilters,
  createEventDate,
  setCreateEventDate,
  calendarMaxDate,
  handleEditClassEvent,
  handleEditAppointmentEvent,
  handleOpenSelectEventModal,
  permission = true,
  setSelectedCoach,
}: IEventCalendarDaySchedulerViewProps): JSX.Element => {
  const { classes } = useStyles();

  const [dateSelected, setDateSelected] = useState<string>(dayjs(createEventDate).format('YYYY-MM-DD'));

  const debouncedDate = useDebounce(dateSelected, 200);
  const debouncedCoaches = useDebounce(searchFilters.coaches, 200);
  const debouncedLocations = useDebounce(searchFilters.locations, 200);
  const debouncedEventTypes = useDebounce(searchFilters.eventTypes, 200);

  const { data: staffData, isSuccess: staffIsSuccess } = useAutoCompleteStaff({
    search: '',
    isCoach: true,
  });

  const allCoachResources: CoachResource[] = useMemo(() => {
    if (!staffIsSuccess || !staffData?.data) return [];

    return (staffData.data as StaffAutoComplete[])
      .filter(s => s?.isCoach)
      .map(s => ({ id: s.staffId, name: s.fullName }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [staffIsSuccess, staffData]);

  const visibleCoaches: CoachResource[] = useMemo(() => {
    const selected = (debouncedCoaches ?? []).filter((c: string) => c !== 'noStaff' && c !== 'None');
    if (!selected.length) return allCoachResources;

    const selectedSet = new Set(selected);
    return allCoachResources.filter(r => selectedSet.has(r.id));
  }, [allCoachResources, debouncedCoaches]);

  const visibleResourceIds = useMemo(() => new Set(visibleCoaches.map(r => r.id)), [visibleCoaches]);

  const resolveCoachName = useCallback(
    (id: string) => visibleCoaches.find(r => r.id === id)?.name,
    [visibleCoaches],
  );

  const { handleEventUpdate, handleEventUpdated, recurrenceModal, notificationModal } = useEventDragUpdate({
    handleEditClassEvent,
    handleEditAppointmentEvent,
    allowDateChange: false,
    resolveCoachName,
  });

  const startOfDay = debouncedDate ? dayjs(debouncedDate).startOf('day').toISOString() : null;
  const endOfDay = debouncedDate ? dayjs(debouncedDate).endOf('day').toISOString() : null;

  const {
    data: availabilityData,
    isSuccess: availabilityIsSuccess,
    isError: availabilityIsError,
  } = useStaffAvailabilityCalendarParallel({
    coachIds: visibleCoaches.map(r => r.id),
    startDate: startOfDay,
    endDate: endOfDay,
    enabled: visibleCoaches.length > 0 && !!debouncedDate,
  });

  const {
    data: classData,
    isSuccess: classIsSuccess,
    isError: classIsError,
  } = useClassAgenda({
    date: debouncedDate,
    coaches: debouncedCoaches,
    locations: debouncedLocations,
    eventTypes: debouncedEventTypes,
  });

  const {
    data: appointmentData,
    isSuccess: appointmentIsSuccess,
    isError: appointmentIsError,
  } = useAppointmentAgenda({
    date: debouncedDate,
    coaches: debouncedCoaches,
    locations: debouncedLocations,
    eventTypes: debouncedEventTypes,
  });

  const eventsStatus = getLoadStatus(
    classIsError || appointmentIsError,
    classIsSuccess && appointmentIsSuccess,
  );

  const coachAvailabilityStatus = getLoadStatus(availabilityIsError, availabilityIsSuccess);

  const calendarEvents: CalendarEventWithMeta[] = useMemo(() => {
    if (!classIsSuccess || !appointmentIsSuccess) return [];

    const classEvents: MbscCalendarEvent[] = (classData?.data ?? []).flatMap((event: KiloClassAgenda) => {
      const start = dayjs(`${event.startDate}T${event.startTime}`).toDate();
      const end = dayjs(`${event.startDate}T${event.startTime}`).add(event.duration, 'minute').toDate();

      return getEventResourceIds(getCoachIdsForClass(event), visibleResourceIds).map(coachId => ({
        id: `class-${event.kiloClassId}-${coachId}`,
        start,
        end,
        title: `${event.kiloClassType?.name ?? 'Class'} ${
          event.athleteKiloClasses.filter(
            akc => (akc.isReserved || akc.isCheckedIn) && !akc.isLateCancel && !akc.isNoShow,
          ).length
        }/${event.capacity}`,
        color: event.kiloClassType?.color,
        resource: coachId,
        eventType: 'class' as const,
        originalId: event.kiloClassId,
        coachId,
        nonDraggable: !isClassDraggable(event),
      }));
    });

    const apptEvents: MbscCalendarEvent[] = (appointmentData?.data ?? []).flatMap(
      (event: AppointmentAgenda) => {
        const start = dayjs(`${event.startDate}T${event.startTime}`).toDate();
        const end = dayjs(`${event.startDate}T${event.startTime}`).add(event.duration, 'minute').toDate();

        return getEventResourceIds(getCoachIdsForAppointment(event), visibleResourceIds).map(coachId => ({
          id: `appt-${event.appointmentId}-${coachId}`,
          start,
          end,
          title: getAppointmentEventTitle(event),
          color: event.appointmentType?.color,
          resource: coachId,
          eventType: 'appointment' as const,
          originalId: event.appointmentId,
          coachId,
          nonDraggable: !isAppointmentDraggable(event),
        }));
      },
    );

    return sortArray([...classEvents, ...apptEvents], { orderBy: 'start', reversed: false });
  }, [appointmentIsSuccess, classIsSuccess, appointmentData, classData, visibleResourceIds]);

  const resources = useMemo(
    () => withNoCoachResource(visibleCoaches, calendarEvents),
    [visibleCoaches, calendarEvents],
  );

  const invalidSlots = useMemo(() => {
    if (!availabilityIsSuccess || !availabilityData) return [];

    return availabilityData.map(slot => ({
      start: slot.start,
      end: slot.end,
      resource: slot.resource,
      title: slot.title,
    }));
  }, [availabilityIsSuccess, availabilityData]);

  const availabilityColors = useMemo(() => {
    if (!availabilityIsSuccess || !debouncedDate) return [];

    const availableSlots = calculateAvailableSlots(
      invalidSlots,
      visibleCoaches.map(r => r.id),
      debouncedDate,
    );

    return availableSlots.map(slot => ({
      start: slot.start,
      end: slot.end,
      resource: slot.resource,
      background: '#10B981',
    }));
  }, [availabilityIsSuccess, invalidSlots, visibleCoaches, debouncedDate]);

  const onPageLoading = useCallback(
    (event: MbscPageLoadingEvent): void => {
      const d = dayjs(event.firstDay);
      setDateSelected(d.format('YYYY-MM-DD'));
      setCreateEventDate(d.toDate());
    },
    [setCreateEventDate],
  );

  const handleEventClick = useCallback(
    (e: MbscEventClickEvent) => {
      const event = e.event as CalendarEventWithMeta;
      const { eventType, originalId } = event;
      const id = originalId?.toString() ?? '';

      if (eventType === 'class') handleEditClassEvent(id);
      if (eventType === 'appointment') handleEditAppointmentEvent(id);
    },
    [handleEditAppointmentEvent, handleEditClassEvent],
  );

  const handleCellClick = useCallback(
    (event: MbscCellClickEvent): void => {
      if (!permission) return;

      setCreateEventDate(event.date);

      const resourceId = event.resource as string | undefined;
      const coach = resourceId ? visibleCoaches.find(r => r.id === resourceId) : undefined;
      setSelectedCoach(coach ? { staffId: coach.id, name: coach.name } : undefined);

      handleOpenSelectEventModal();
    },
    [handleOpenSelectEventModal, permission, setCreateEventDate, setSelectedCoach, visibleCoaches],
  );

  if (appointmentIsError || classIsError) {
    return <>Something went wrong</>;
  }

  return (
    <>
      <Eventcalendar
        renderHeader={() => eventCalendarNavigationButtons(eventsStatus, coachAvailabilityStatus)}
        selectedDate={createEventDate}
        theme='ios'
        themeVariant='light'
        className={classes.calendar}
        view={{ schedule: { type: 'day', allDay: false } }}
        data={calendarEvents}
        resources={resources}
        dragToMove={permission}
        dragToResize={permission}
        onEventUpdate={handleEventUpdate}
        onEventUpdated={handleEventUpdated}
        onPageLoading={onPageLoading}
        onEventClick={handleEventClick}
        onCellDoubleClick={handleCellClick}
        max={calendarMaxDate}
        width='100%'
        invalid={invalidSlots}
        colors={availabilityColors}
      />
      <ModalDragRecurrenceConfirmation {...recurrenceModal} />
      <ModalDragNotificationConfirmation {...notificationModal} />
    </>
  );
};

export default EventCalendarDaySchedulerView;
